const GUEST_KEY = "aidc:guest:"

/*
 * Who this browser is when it joins a session as a guest.
 *
 * A guest does not sign in. They type a name and this browser mints them an id,
 * which becomes their participant document id and is remembered so a reload —
 * or a tab closed and reopened from the same link — walks back into the seat
 * they already have instead of adding a second row to the roster.
 *
 * localStorage, not sessionStorage: a workshop outlives a tab, and a guest who
 * closes the window and comes back must not lose the pain points attributed to
 * them. The cost is that every tab of this browser is the same guest, which is
 * correct — it is one person at one session.
 *
 * There is no facilitator marker here any more. The facilitator is a signed-in
 * Google account listed in `session.facilitatorIds`; nothing about that is
 * local, so nothing about it can be claimed by editing storage. This file is
 * therefore not a security boundary and never was — it only answers "have I
 * been here before".
 */

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage can be unavailable (private mode, quota). Identity then lasts
    // only for this page view, which is degraded but not broken.
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

/**
 * A new guest id.
 *
 * Generated on the client, before anything is written, so the id is known while
 * the join is still in flight and the same value can be stored, used as the
 * document id and attached to whatever they write next. `crypto.randomUUID` is
 * the source where it exists; the fallback is only for old or insecure contexts,
 * where a collision would mean two guests sharing a seat — vanishingly unlikely
 * across one room, which is the only scope that matters.
 */
export function newGuestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `g-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

export function getStoredParticipantId(sessionId: string): string | null {
  return read(GUEST_KEY + sessionId)
}

export function setStoredParticipantId(sessionId: string, participantId: string): void {
  write(GUEST_KEY + sessionId, participantId)
}

export function clearStoredParticipantId(sessionId: string): void {
  remove(GUEST_KEY + sessionId)
}

/**
 * The sessions this browser has joined as a guest.
 *
 * Unlike the old per-tab marker there can legitimately be several: a guest may
 * have sat in more than one workshop from the same browser, and neither entry
 * invalidates the other. Sorted so the answer never varies between browsers,
 * whose storage key order is unspecified.
 */
export function storedGuestSessions(): string[] {
  const sessions: string[] = []
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (key?.startsWith(GUEST_KEY)) sessions.push(key.slice(GUEST_KEY.length))
    }
  } catch {
    // No storage: read as "never joined anything", the same as a fresh browser.
  }
  return sessions.sort()
}
